import React from "react";
import { Box, Heading, Tooltip } from "@chakra-ui/react";
import Card from "@/src/components/card/Card";
import { useQuery } from "@tanstack/react-query";
import { Get } from "@/src/api";
import LoadingIndicator from "@/src/components/loading";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/src/components/ui/table";
import { Button } from "@/src/components/ui/button";
import { Redirect, useHistory } from "react-router-dom";
import useCoinLogsAccess from "@/src/hooks/useCoinLogsAccess";

type SupportAccountManager = {
  id: number;
  firstName: string;
  lastName: string;
};

type CoinLog = {
  id: number;
  supportAccountManager: SupportAccountManager;
  packageName: string;
  note: string;
  coinAmount: number;
  userId: number;
};

type CoinLogsResponse = {
  logs: CoinLog[];
  total: number;
};

const MAX_NOTE_LENGTH = 50;
const LIMIT = 20;

export default function CoinLogs() {
  const [page, setPage] = React.useState(1);
  const history = useHistory();
  const hasCoinLogsAccess = useCoinLogsAccess();

  const { data, isLoading, error } = useQuery<CoinLogsResponse>({
    queryKey: ["coinLogs", page],
    queryFn: async () => {
      return Get(`/support/admin/coin-change-logs?page=${page}&limit=${LIMIT}`);
    },
  });

  const totalPages = data ? Math.ceil(data.total / LIMIT) : 0;
  const hasNextPage = page < totalPages;

  const handlePreviousPage = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (hasNextPage) {
      setPage(page + 1);
    }
  };

  const shouldShowTooltip = (text: string) => {
    return text.length > MAX_NOTE_LENGTH;
  };

  const truncateText = (text: string) => {
    if (text.length <= MAX_NOTE_LENGTH) return text;
    return `${text.slice(0, MAX_NOTE_LENGTH)}...`;
  };

  if (!hasCoinLogsAccess) {
    return <Redirect to={"/"} />;
  }

  if (isLoading) {
    return <LoadingIndicator />;
  }

  if (error) {
    return (
      <Card>
        <Box p={4}>
          <Heading size="md" color="red.500">
            Error loading coin logs
          </Heading>
          <Box mt={2}>{(error as Error).message}</Box>
        </Box>
      </Card>
    );
  }

  return (
    <Card>
      <Box p={4}>
        <Heading size="md" mb={4}>
          Coin Logs
        </Heading>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>ID</TableHead>
              <TableHead>User ID</TableHead>
              <TableHead>Account Manager</TableHead>
              <TableHead>Package Name</TableHead>
              <TableHead className="w-[200px]">Note</TableHead>
              <TableHead>Coin Amount</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data?.logs.map((log: CoinLog) => (
              <TableRow key={log.id}>
                <TableCell>{log.id}</TableCell>
                <TableCell>
                  <Button
                    variant="link"
                    onClick={() => history.push(`/users?userId=${log.userId}`)}
                  >
                    {log.userId}
                  </Button>
                </TableCell>
                <TableCell>
                  {log.supportAccountManager.firstName}{" "}
                  {log.supportAccountManager.lastName}
                </TableCell>
                <TableCell>{log.packageName}</TableCell>
                <TableCell className="max-w-[200px] truncate">
                  {shouldShowTooltip(log.note ?? "") ? (
                    <Tooltip label={log.note ?? ""} placement="top">
                      <span>{truncateText(log.note ?? "")}</span>
                    </Tooltip>
                  ) : (
                    log.note
                  )}
                </TableCell>
                <TableCell>{log.coinAmount}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Box mt={4} display="flex" justifyContent="flex-end" gap={2}>
          <Button
            onClick={handlePreviousPage}
            disabled={page === 1}
            variant="outline"
          >
            Previous
          </Button>
          <Button
            onClick={handleNextPage}
            disabled={!hasNextPage}
            variant="outline"
          >
            Next
          </Button>
        </Box>
      </Box>
    </Card>
  );
}
